import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_ENDPOINTS } from '../config/api';
import { AdminPageHeader, AdminPageShell, NoticePanel, StatCard } from './AdminPageUI';

const DEFAULT_GPS_SETTINGS = {
  enabled: true,
  teacherAutoCheckIn: true,
  staffAutoCheckIn: true,
  latitude: '',
  longitude: '',
  rangeMeters: 15.24,
  accuracyMeters: 20,
  updatedAt: null
};

const normalizeGpsSettings = (settings = {}) => ({
  enabled: settings.enabled ?? DEFAULT_GPS_SETTINGS.enabled,
  teacherAutoCheckIn: settings.teacherAutoCheckIn ?? DEFAULT_GPS_SETTINGS.teacherAutoCheckIn,
  staffAutoCheckIn: settings.staffAutoCheckIn ?? DEFAULT_GPS_SETTINGS.staffAutoCheckIn,
  latitude: settings.latitude ?? DEFAULT_GPS_SETTINGS.latitude,
  longitude: settings.longitude ?? DEFAULT_GPS_SETTINGS.longitude,
  rangeMeters: settings.rangeMeters ?? DEFAULT_GPS_SETTINGS.rangeMeters,
  accuracyMeters: settings.accuracyMeters ?? DEFAULT_GPS_SETTINGS.accuracyMeters,
  updatedAt: settings.updatedAt || DEFAULT_GPS_SETTINGS.updatedAt
});

const GeneralAttendanceDashboard = () => {
  const navigate = useNavigate();
  const [summaryData, setSummaryData] = useState(null);
  const [detailedData, setDetailedData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [showQuickMark, setShowQuickMark] = useState(false);
  const [showTimingSettings, setShowTimingSettings] = useState(false);
  const [showGpsSettings, setShowGpsSettings] = useState(false);
  const [timingConfig, setTimingConfig] = useState(null);
  const [currentTimings, setCurrentTimings] = useState(null);
  const [gpsSettings, setGpsSettings] = useState(DEFAULT_GPS_SETTINGS);
  const [gpsMessage, setGpsMessage] = useState(null);
  const [savingGpsSettings, setSavingGpsSettings] = useState(false);

  // Form state for timing settings
  const [timingForm, setTimingForm] = useState({
    morning_start: '08:00',
    late_threshold: '08:30',
    afternoon_end: '15:00',
    working_days: 'weekdays',
    student_grace_period: '30',
    teacher_grace_period: '15',
    staff_grace_period: '15'
  });

  useEffect(() => {
    loadDashboardData();
    loadGpsSettings();
  }, []);

  useEffect(() => {
    loadDetailedData();
  }, [selectedDate, filterType]);

  // Load current timings when settings panel opens
  const loadCurrentTimings = async () => {
    try {
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/timings/config`);
      if (response.ok) {
        const timings = await response.json();
        setCurrentTimings(timings);
        // Also update the form with current values
        setTimingForm({
          morning_start: timings.morning_start || '08:00',
          late_threshold: timings.late_threshold || '08:30',
          afternoon_end: timings.afternoon_end || '15:00',
          working_days: Array.isArray(timings.working_days) && timings.working_days.length === 7 ? 'all' : 'weekdays',
          student_grace_period: timings.student_grace_period?.toString() || '30',
          teacher_grace_period: timings.teacher_grace_period?.toString() || '15',
          staff_grace_period: timings.staff_grace_period?.toString() || '15'
        });
      }
    } catch (error) {
      console.error('Error loading current timings:', error);
    }
  };

  const loadGpsSettings = async () => {
    try {
      const response = await fetch(API_ENDPOINTS.ATTENDANCE_GPS_SETTINGS);
      const settings = await response.json();

      if (!response.ok) {
        throw new Error(settings.error || 'Failed to load GPS settings');
      }

      setGpsSettings(normalizeGpsSettings(settings));
      setGpsMessage(null);
    } catch (error) {
      console.error('Error loading GPS settings:', error);
      setGpsMessage({ type: 'error', text: 'GPS settings could not be loaded.' });
    }
  };

  const saveGpsSettings = async () => {
    const requiredFields = ['latitude', 'longitude', 'rangeMeters', 'accuracyMeters'];
    const hasMissingField = requiredFields.some((key) => String(gpsSettings[key] ?? '').trim() === '');

    if (hasMissingField) {
      setGpsMessage({ type: 'error', text: 'Latitude, longitude, radius, and accuracy are required.' });
      return;
    }

    const parsedSettings = {
      enabled: Boolean(gpsSettings.enabled),
      teacherAutoCheckIn: Boolean(gpsSettings.teacherAutoCheckIn),
      staffAutoCheckIn: Boolean(gpsSettings.staffAutoCheckIn),
      latitude: Number(gpsSettings.latitude),
      longitude: Number(gpsSettings.longitude),
      rangeMeters: Number(gpsSettings.rangeMeters),
      accuracyMeters: Number(gpsSettings.accuracyMeters)
    };

    if (
      !Number.isFinite(parsedSettings.latitude) ||
      !Number.isFinite(parsedSettings.longitude) ||
      parsedSettings.rangeMeters <= 0 ||
      parsedSettings.accuracyMeters <= 0
    ) {
      setGpsMessage({ type: 'error', text: 'Enter valid GPS coordinates, radius, and accuracy values.' });
      return;
    }

    setSavingGpsSettings(true);
    setGpsMessage(null);

    try {
      const response = await fetch(API_ENDPOINTS.ATTENDANCE_GPS_SETTINGS, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(parsedSettings)
      });
      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Failed to save GPS settings');
      }

      setGpsSettings(normalizeGpsSettings(result.settings));
      setGpsMessage({
        type: 'success',
        text: `GPS settings saved. Teacher sign-in radius is now ${result.settings.rangeMeters}m.`
      });
    } catch (error) {
      console.error('Error saving GPS settings:', error);
      setGpsMessage({ type: 'error', text: error.message || 'Failed to save GPS settings.' });
    } finally {
      setSavingGpsSettings(false);
    }
  };

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/dashboard/summary`);
      if (response.ok) {
        const data = await response.json();
        setSummaryData(data);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailedData = async () => {
    try {
      const params = new URLSearchParams({
        date: selectedDate,
        limit: '100'
      });
      if (filterType !== 'all') {
        params.append('type', filterType);
      }

      const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/dashboard/details?${params}`);
      if (response.ok) {
        const data = await response.json();
        setDetailedData(data.data);
      }
    } catch (error) {
      console.error('Error loading detailed data:', error);
    }
  };

  const markAttendance = async (userId, userType, status) => {
    try {
      let endpoint = '';
      let payload = {};

      if (userType === 'student') {
        endpoint = `${API_ENDPOINTS.ATTENDANCE}`;
        payload = {
          username: userId,
          date: selectedDate,
          status,
          markedBy: 'admin'
        };
      } else if (userType === 'teacher') {
        endpoint = `${API_ENDPOINTS.BASE_URL}/api/teachers/${userId}/attendance`;
        payload = {
          date: selectedDate,
          status,
          checkInTime: status === 'present' ? new Date().toTimeString().split(' ')[0] : null,
          markedBy: 'admin'
        };
      } else if (userType === 'staff') {
        endpoint = `${API_ENDPOINTS.BASE_URL}/api/staff/${userId}/attendance`;
        payload = {
          date: selectedDate,
          status,
          checkInTime: status === 'present' ? new Date().toTimeString().split(' ')[0] : null,
          markedBy: 'admin'
        };
      }

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        loadDetailedData();
        loadDashboardData();
        alert('Attendance marked successfully!');
      } else {
        alert('Failed to mark attendance');
      }
    } catch (error) {
      console.error('Error marking attendance:', error);
      alert('Failed to mark attendance');
    }
  };

  const getFilteredData = () => {
    let filtered = detailedData;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(item => item.status === filterStatus);
    }

    if (searchTerm) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.class?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.department?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return filtered;
  };

  const getStatusBadge = (status, record) => {
    // Special highlighting for late arrivals
    if (status === 'late') {
      return 'bg-orange-100 text-orange-800 border-2 border-orange-300 animate-pulse';
    }

    const badges = {
      present: 'bg-green-100 text-green-800',
      absent: 'bg-red-100 text-red-800',
      'not-marked': 'bg-gray-100 text-gray-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const isLateArrival = (record) => {
    return record.status === 'late';
  };

  const exportReport = () => {
    const filteredData = getFilteredData();
    const csvData = [
      ['Name', 'Type', 'Class/Department', 'Status', 'Check In', 'Check Out', 'Hours Worked', 'Notes'],
      ...filteredData.map(item => [
        item.name,
        item.type,
        item.class || item.department,
        item.status,
        item.checkInTime || '',
        item.checkOutTime || '',
        item.hoursWorked || '',
        item.notes || ''
      ])
    ];

    const csvContent = csvData.map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `attendance-report-${selectedDate}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <div className="text-xl text-gray-600">Loading attendance dashboard...</div>
        </div>
      </div>
    );
  }

  return (
    <AdminPageShell>
        <AdminPageHeader
          eyebrow="Operations"
          title="General Attendance Dashboard"
          description="Unified attendance monitoring for students, teachers, and staff with daily filters, late-arrival visibility, and export-ready records."
          actions={(
          <button
            onClick={() => navigate('/dashboard')}
            className="bg-slate-800 text-white px-4 py-2 rounded-lg hover:bg-slate-700"
          >
            Back to Dashboard
          </button>
          )}
        />

        <NoticePanel title="Attendance triage" tone="amber">
          Prioritize unmarked records, late arrivals, and absent students first. Use the date and person-type filters before exporting a daily report.
        </NoticePanel>

        {/* Summary Cards */}
        {summaryData && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Overall Summary */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Overall Today</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {summaryData.today.overall.presentPercentage}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {summaryData.today.overall.present} / {summaryData.today.overall.total} present
                  </p>
                </div>
                <div className="text-right">
                  <div className="text-xs text-red-600">Absent: {summaryData.today.overall.absent}</div>
                  <div className="text-xs text-yellow-600">Late: {summaryData.today.overall.late}</div>
                  <div className="text-xs text-gray-600">Unmarked: {summaryData.today.overall.unmarked}</div>
                </div>
              </div>
            </div>

            {/* Students */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Students</p>
                  <p className="text-2xl font-bold text-blue-600">
                    {summaryData.today.students.presentPercentage}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {summaryData.today.students.present} / {summaryData.today.students.total}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Teachers */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Teachers</p>
                  <p className="text-2xl font-bold text-green-600">
                    {summaryData.today.teachers.presentPercentage}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {summaryData.today.teachers.present} / {summaryData.today.teachers.total}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                  </svg>
                </div>
              </div>
            </div>

            {/* Staff */}
            <div className="bg-white rounded-lg shadow p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Staff</p>
                  <p className="text-2xl font-bold text-purple-600">
                    {summaryData.today.staff.presentPercentage}%
                  </p>
                  <p className="text-sm text-gray-500">
                    {summaryData.today.staff.present} / {summaryData.today.staff.total}
                  </p>
                </div>
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                  <svg className="w-6 h-6 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Controls */}
        <div className="bg-white rounded-lg shadow mb-6">
          <div className="p-6">
            <div className="flex flex-wrap gap-4 items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Date</label>
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="border rounded px-3 py-2"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Type</label>
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="border rounded px-3 py-2"
                >
                  <option value="all">All Types</option>
                  <option value="students">Students</option>
                  <option value="teachers">Teachers</option>
                  <option value="staff">Staff</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Filter by Status</label>
                <select
                  value={filterStatus}
                  onChange={(e) => setFilterStatus(e.target.value)}
                  className="border rounded px-3 py-2"
                >
                  <option value="all">All Status</option>
                  <option value="present">Present</option>
                  <option value="absent">Absent</option>
                  <option value="late">Late</option>
                  <option value="not-marked">Not Marked</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Search</label>
                <input
                  type="text"
                  placeholder="Search by name, class, or department..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="border rounded px-3 py-2 w-64"
                />
              </div>

              <div className="flex gap-2">
                <button
                  onClick={exportReport}
                  className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                >
                  Export CSV
                </button>
                <button
                  onClick={loadDashboardData}
                  className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Detailed Attendance Table */}
        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-3 mb-4">
              <h2 className="text-xl font-semibold text-gray-900">
                Attendance Details ({getFilteredData().length} records)
              </h2>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={async () => {
                    const nextOpen = !showGpsSettings;
                    setShowGpsSettings(nextOpen);
                    if (nextOpen) {
                      await loadGpsSettings();
                    }
                  }}
                  className="bg-sky-700 text-white px-4 py-2 rounded hover:bg-sky-800"
                >
                  GPS Radius
                </button>
                <button
                  onClick={async () => {
                    await loadCurrentTimings();
                    setShowTimingSettings(!showTimingSettings);
                  }}
                  className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                >
                  Timing Settings
                </button>
                <button
                  onClick={() => setShowQuickMark(!showQuickMark)}
                  className="bg-indigo-600 text-white px-4 py-2 rounded hover:bg-indigo-700"
                >
                  Quick Mark Attendance
                </button>
              </div>
            </div>

            {/* Timing Settings Panel */}
            {showTimingSettings && (
              <div className="mb-6 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h3 className="text-lg font-medium text-gray-900 mb-3">School Timing Settings</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Configure school hours and grace periods for automatic late detection.
                </p>

                {/* Current Settings Display */}
                <div className="bg-white rounded p-4 mb-4">
                  <h4 className="font-medium text-gray-800 mb-3">Current Settings:</h4>
                  {currentTimings ? (
                    <>
                      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
                        <div>
                          <span className="font-medium text-gray-700">Morning Start:</span>
                          <div className="text-purple-600 font-mono">{currentTimings.morning_start || '08:00'}</div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Late Threshold:</span>
                          <div className="text-orange-600 font-mono">{currentTimings.late_threshold || '08:30'}</div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Afternoon End:</span>
                          <div className="text-blue-600 font-mono">{currentTimings.afternoon_end || '15:00'}</div>
                        </div>
                        <div>
                          <span className="font-medium text-gray-700">Working Days:</span>
                          <div className="text-green-600">
                            {Array.isArray(currentTimings.working_days) && currentTimings.working_days.length === 7 ? 'Mon-Sun' : 'Mon-Fri'}
                          </div>
                        </div>
                      </div>

                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <h5 className="font-medium text-gray-700 mb-2">Grace Periods:</h5>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <span className="text-gray-600">Students:</span>
                            <span className="text-blue-600 ml-2">{currentTimings.student_grace_period || 30} minutes</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Teachers:</span>
                            <span className="text-green-600 ml-2">{currentTimings.teacher_grace_period || 15} minutes</span>
                          </div>
                          <div>
                            <span className="text-gray-600">Staff:</span>
                            <span className="text-purple-600 ml-2">{currentTimings.staff_grace_period || 15} minutes</span>
                          </div>
                        </div>
                      </div>
                    </>
                  ) : (
                    <div className="text-gray-500 italic">Loading current settings...</div>
                  )}
                </div>

                {/* Settings Form */}
                <div className="bg-white rounded p-4">
                  <h4 className="font-medium text-gray-800 mb-3">Update Settings:</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Morning Start</label>
                      <input
                        type="time"
                        value={timingForm.morning_start}
                        onChange={(e) => setTimingForm({...timingForm, morning_start: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Late Threshold</label>
                      <input
                        type="time"
                        value={timingForm.late_threshold}
                        onChange={(e) => setTimingForm({...timingForm, late_threshold: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Afternoon End</label>
                      <input
                        type="time"
                        value={timingForm.afternoon_end}
                        onChange={(e) => setTimingForm({...timingForm, afternoon_end: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Working Days</label>
                      <select
                        value={timingForm.working_days}
                        onChange={(e) => setTimingForm({...timingForm, working_days: e.target.value})}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="weekdays">Mon-Fri</option>
                        <option value="all">Mon-Sun</option>
                        <option value="custom">Custom</option>
                      </select>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200">
                    <h5 className="font-medium text-gray-700 mb-3">Grace Periods (minutes):</h5>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Students</label>
                        <input
                          type="number"
                          value={timingForm.student_grace_period}
                          onChange={(e) => setTimingForm({...timingForm, student_grace_period: e.target.value})}
                          min="0"
                          max="120"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Teachers</label>
                        <input
                          type="number"
                          value={timingForm.teacher_grace_period}
                          onChange={(e) => setTimingForm({...timingForm, teacher_grace_period: e.target.value})}
                          min="0"
                          max="120"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Staff</label>
                        <input
                          type="number"
                          value={timingForm.staff_grace_period}
                          onChange={(e) => setTimingForm({...timingForm, staff_grace_period: e.target.value})}
                          min="0"
                          max="120"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-4">
                    <button
                      onClick={async () => {
                        const timingData = {
                          morning_start: timingForm.morning_start,
                          late_threshold: timingForm.late_threshold,
                          afternoon_end: timingForm.afternoon_end,
                          working_days: timingForm.working_days,
                          student_grace_period: timingForm.student_grace_period,
                          teacher_grace_period: timingForm.teacher_grace_period,
                          staff_grace_period: timingForm.staff_grace_period,
                          timezone: 'Africa/Lagos'
                        };

                        console.log('Frontend Frontend: Sending timing update:', timingData);

                        try {
                          const response = await fetch(`${API_ENDPOINTS.BASE_URL}/api/attendance/timings/config`, {
                            method: 'PUT',
                            headers: {
                              'Content-Type': 'application/json',
                            },
                            body: JSON.stringify(timingData)
                          });

                          console.log('Response Frontend: Response status:', response.status);
                          const result = await response.json();
                          console.log('Response Frontend: Response data:', result);

                          if (response.ok) {
                            alert('OK School timings updated successfully! Changes applied immediately.');
                            setShowTimingSettings(false);
                            // Optionally refresh the page or update state
                            window.location.reload();
                          } else {
                            alert(`X Failed to update settings: ${result.error}`);
                          }
                        } catch (error) {
                          console.error('X Frontend: Error saving timing settings:', error);
                          alert('X Failed to save timing settings. Please try again.');
                        }
                      }}
                      className="bg-purple-600 text-white px-4 py-2 rounded hover:bg-purple-700"
                    >
                      Save Settings
                    </button>
                    <button
                      onClick={() => setShowTimingSettings(false)}
                      className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* GPS Settings Panel */}
            {showGpsSettings && (
              <div className="mb-6 p-4 bg-sky-50 rounded-lg border border-sky-200">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-medium text-gray-900">GPS Attendance Settings</h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Controls the school location and sign-in radius used by teacher and staff GPS check-in.
                    </p>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
                    <div className="bg-white border border-sky-100 rounded-lg px-4 py-3">
                      <div className="font-medium text-gray-600">Radius</div>
                      <div className="text-sky-700 font-bold">{gpsSettings.rangeMeters || '-'}m</div>
                    </div>
                    <div className="bg-white border border-sky-100 rounded-lg px-4 py-3">
                      <div className="font-medium text-gray-600">Accuracy</div>
                      <div className="text-sky-700 font-bold">{gpsSettings.accuracyMeters || '-'}m</div>
                    </div>
                    <div className="bg-white border border-sky-100 rounded-lg px-4 py-3">
                      <div className="font-medium text-gray-600">Last Saved</div>
                      <div className="text-sky-700 font-bold">
                        {gpsSettings.updatedAt ? new Date(gpsSettings.updatedAt).toLocaleString() : 'Not saved'}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Latitude</label>
                    <input
                      type="number"
                      step="any"
                      value={gpsSettings.latitude}
                      onChange={(e) => setGpsSettings(current => ({ ...current, latitude: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">School Longitude</label>
                    <input
                      type="number"
                      step="any"
                      value={gpsSettings.longitude}
                      onChange={(e) => setGpsSettings(current => ({ ...current, longitude: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Teacher Sign-in Radius (meters)</label>
                    <input
                      type="number"
                      min="1"
                      step="0.01"
                      value={gpsSettings.rangeMeters}
                      onChange={(e) => setGpsSettings(current => ({ ...current, rangeMeters: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Required GPS Accuracy (meters)</label>
                    <input
                      type="number"
                      min="1"
                      step="1"
                      value={gpsSettings.accuracyMeters}
                      onChange={(e) => setGpsSettings(current => ({ ...current, accuracyMeters: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-sky-500"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-4">
                  {[
                    ['enabled', 'Enable GPS validation'],
                    ['teacherAutoCheckIn', 'Teacher auto check-in'],
                    ['staffAutoCheckIn', 'Staff auto check-in']
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-3 bg-white border border-sky-100 rounded-lg p-4">
                      <input
                        type="checkbox"
                        checked={Boolean(gpsSettings[key])}
                        onChange={(e) => setGpsSettings(current => ({ ...current, [key]: e.target.checked }))}
                      />
                      <span className="font-semibold text-sky-900">{label}</span>
                    </label>
                  ))}
                </div>

                {gpsMessage && (
                  <div className={`mt-4 rounded-md border px-4 py-3 text-sm font-medium ${
                    gpsMessage.type === 'success'
                      ? 'bg-emerald-50 border-emerald-200 text-emerald-800'
                      : 'bg-rose-50 border-rose-200 text-rose-800'
                  }`}>
                    {gpsMessage.text}
                  </div>
                )}

                <div className="flex justify-end gap-3 mt-4">
                  <button
                    onClick={saveGpsSettings}
                    disabled={savingGpsSettings}
                    className={`px-4 py-2 rounded text-white ${
                      savingGpsSettings ? 'bg-sky-300 cursor-not-allowed' : 'bg-sky-700 hover:bg-sky-800'
                    }`}
                  >
                    {savingGpsSettings ? 'Saving...' : 'Save GPS Settings'}
                  </button>
                  <button
                    onClick={() => setShowGpsSettings(false)}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                  >
                    Close
                  </button>
                </div>
              </div>
            )}

            {/* Quick Mark Panel */}
            {showQuickMark && (
              <div className="mb-6 p-4 bg-indigo-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900 mb-3">Quick Attendance Marking</h3>
                <p className="text-sm text-gray-600 mb-4">
                  Select multiple records below to mark attendance in bulk.
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      const unmarkedRecords = getFilteredData().filter(item => item.status === 'not-marked');
                      unmarkedRecords.forEach(record => {
                        markAttendance(record.userId, record.type, 'present');
                      });
                    }}
                    className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
                  >
                    Mark All Unmarked as Present
                  </button>
                  <button
                    onClick={() => setShowQuickMark(false)}
                    className="bg-gray-600 text-white px-4 py-2 rounded hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Attendance Table */}
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">User</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Type</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Class/Dept</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Check In</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Check Out</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredData().map((record) => (
                    <tr key={record.id} className="border-t">
                      <td className="px-4 py-3">
                        <div className="flex items-center space-x-3">
                          <img
                            src={record.profilePicture || '/fallback-data/default-avatar.png'}
                            alt={record.name}
                            className="w-8 h-8 rounded-full object-cover"
                            onError={(e) => {
                              e.target.src = '/fallback-data/default-avatar.png';
                            }}
                          />
                          <div>
                            <div className="text-sm font-medium text-gray-900">{record.name}</div>
                            {record.verified && (
                              <div className="text-xs text-green-600">? Verified</div>
                            )}
                            {record.autoChecked && (
                              <div className="text-xs text-blue-600">Auto Auto</div>
                            )}
                            {record.locationValidated && (
                              <div className="text-xs text-purple-600">GPS GPS</div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          record.type === 'student' ? 'bg-blue-100 text-blue-800' :
                          record.type === 'teacher' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {record.type.charAt(0).toUpperCase() + record.type.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {record.class || record.department}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getStatusBadge(record.status)}`}>
                          {record.status === 'not-marked' ? 'Not Marked' : record.status.charAt(0).toUpperCase() + record.status.slice(1)}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {record.checkInTime || '-'}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {record.checkOutTime || '-'}
                        {record.hoursWorked && (
                          <div className="text-xs text-gray-500">
                            ({record.hoursWorked}h)
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex space-x-2">
                          {record.status === 'not-marked' && (
                            <>
                              <button
                                onClick={() => markAttendance(record.userId, record.type, 'present')}
                                className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs hover:bg-green-200"
                              >
                                Present
                              </button>
                              <button
                                onClick={() => markAttendance(record.userId, record.type, 'absent')}
                                className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs hover:bg-red-200"
                              >
                                Absent
                              </button>
                              <button
                                onClick={() => markAttendance(record.userId, record.type, 'late')}
                                className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs hover:bg-yellow-200"
                              >
                                Late
                              </button>
                            </>
                          )}
                          {record.status !== 'not-marked' && (
                            <button
                              onClick={() => markAttendance(record.userId, record.type, 'not-marked')}
                              className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs hover:bg-gray-200"
                            >
                              Clear
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {getFilteredData().length === 0 && (
              <div className="text-center py-8">
                <div className="text-xl text-gray-500">No attendance records found for the selected filters</div>
              </div>
            )}
          </div>
        </div>
    </AdminPageShell>
  );
};

export default GeneralAttendanceDashboard;
